import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { FaPlus, FaMinus } from "react-icons/fa";
import { useAuth } from "../../lib/AuthContext";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const rowSchema = z.object({
  userName: z.string().trim().min(1, "Name is required"),
  email: z.string().trim().email("Enter a valid email"),
});

const schema = z.object({
  recipients: z.array(rowSchema).min(1, "At least one recipient is required"),
});

type FormValues = z.infer<typeof schema>;

interface Row {
  userName: string;
  email: string;
}

// interface FormValues {
//   recipients: Row[];
// }

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendMails: React.FC = () => {
  const templateOptions = [
    { label: "Launch-1", value: "/email/launch-email-1.svg" },
    { label: "Feedback", value: "/email/feedback.svg" },
  ];
  const apiURL = process.env.REACT_APP_API_URL;
  const { hasAuthenticated } = useAuth();
  const [selectedMail, setSelectedMail] = useState({ label: "Launch-1", value: "/email/launch-email-1.svg" });

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, errors },
  } = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      recipients: [{ userName: "", email: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "recipients",
  });

  const canAddAt = (index: number) => {
    const row = watch(`recipients.${index}`) as Row | undefined;
    return Boolean(
      row &&
      row.userName?.trim() &&
      row.email?.trim() &&
      emailRegex.test(row.email)
    );
  };

  const onSubmit = async (values: FormValues) => {
    if (!hasAuthenticated) return;
    try {
      const res = await fetch(`${apiURL}/send-launch-mail`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Token: hasAuthenticated,
        },
        body: JSON.stringify({ data: values.recipients, mail_type: selectedMail.label }),
      });

      if (!res.ok) throw new Error("Failed to send emails");

      await res.json();
      alert("Emails sent successfully!");
    } catch (err) {
      console.error("Error:", err);
      alert("Failed to send emails");
    }
  };

  const handleSelectValue = (selected_value:any) => {
    const selected = templateOptions.find(option => option.value === selected_value);
    if (selected) {
      setSelectedMail(selected);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      {/* Dropdown for template selection */}
      <div className="flex justify-between mt-4">
        <div className="mb-4">
          <select
            value={selectedMail.value}
            onChange={(e) => handleSelectValue(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded"
          >
            {templateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !isValid}
            className="px-6 py-2 bg-[#2E2E2E] text-white rounded mb-6 disabled:opacity-50"
          >
            {isSubmitting ? "Sending..." : "SEND"}
          </button>
        </div>
      </div>

      <div className="flex flex-col py-8 w-full">
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-start gap-2 mb-4 w-full">
            <div className="flex-1">
              <input
                placeholder="Name"
                className="px-3 py-2 border border-gray-300 rounded w-full"
                {...register(`recipients.${index}.userName`)}
              />
              {errors.recipients?.[index]?.userName?.message && (
                <p className="text-sm text-red-600">
                  {errors.recipients[index]?.userName?.message}
                </p>
              )}
            </div>

            <div className="flex-1">
              <input
                placeholder="Email"
                className="px-3 py-2 border border-gray-300 rounded w-full"
                {...register(`recipients.${index}.email`)}
              />
              {errors.recipients?.[index]?.email?.message && (
                <p className="text-sm text-red-600">
                  {errors.recipients[index]?.email?.message}
                </p>
              )}
            </div>

            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full mt-1"
              >
                <FaMinus />
              </button>
            )}

            {index === fields.length - 1 && (
              <button
                type="button"
                onClick={() => append({ userName: "", email: "" })}
                disabled={!canAddAt(index)}
                className={`p-2 rounded-full text-white mt-1 ${
                  canAddAt(index)
                    ? "bg-gray-700 hover:bg-gray-800"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                <FaPlus />
              </button>
            )}
          </div>
        ))}

        {typeof errors.recipients?.root?.message === "string" && (
          <p className="text-sm text-red-600">
            {errors.recipients.root.message}
          </p>
        )}

        <img
          src={selectedMail.value}
          alt="emailer preview"
          className="mt-6 w-full rounded shadow-lg"
        />
      </div>
    </form>
  );
};

export default SendMails;
